import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Modal,
  Pressable,
  Dimensions,
} from 'react-native';

import {
  getOptionalScopePickerCatalogEntries,
  isScopePickerComingSoon,
  type RequestFormStepId,
} from '../config/requestFormStepRegistry';
import { REQUEST_FORM_SIDEBAR_HEADER_CONTROL_HEIGHT } from '../constants/requestFormLayout';
import { useRequestScope } from '../contexts/RequestScopeContext';
import {
  ADD_SCOPE_MENU_ID,
  SCOPE_MENU_ROOT_ATTR,
  useScopeMenuCoordination,
} from '../contexts/ScopeMenuCoordinationContext';
import { colors } from '../theme/colors';
import { spacing } from '../theme/spacing';
import { lineHeight } from '../theme/typography';
import { filterAddableOptionalStepIdsByVisibility } from '../utils/sidebarScopeStepVisibility';

interface AddScopeControlProps {
  disabled?: boolean;
  stepVisibility?: Readonly<Record<string, boolean | undefined>>;
}

type ScopePickerRowState = 'addable' | 'added' | 'coming-soon';

const ADD_SCOPE_MENU_MARGIN_TOP = 6;
const ADD_SCOPE_MENU_EDGE = 8;
/** Rough max height for flip-above logic (picker scrolls inside panel). */
const ADD_SCOPE_MENU_ESTIMATED_HEIGHT = 320;

const scheduleMenuPositionUpdate = (update: () => void) => {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(update);
    return;
  }
  setTimeout(update, 0);
};

function isFiniteAnchorRect(
  x: number,
  y: number,
  width: number,
  height: number,
): boolean {
  return [x, y, width, height].every(
    value => typeof value === 'number' && Number.isFinite(value),
  );
}

function isPlausibleAnchorRect(
  x: number,
  y: number,
  width: number,
  height: number,
): boolean {
  if (!isFiniteAnchorRect(x, y, width, height)) {
    return false;
  }
  if (width <= 0 || height <= 0) {
    return false;
  }
  // Android can report (0,0) before layout; sidebar header is never at origin.
  if (x <= 0 && y <= 0) {
    return false;
  }
  return true;
}

function measureAnchorInWindow(
  anchor: View,
  callback: (rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => boolean,
): void {
  const tryApply = (
    x: number,
    y: number,
    width: number,
    height: number,
  ): boolean => {
    if (!isPlausibleAnchorRect(x, y, width, height)) {
      return false;
    }
    return callback({ x, y, width, height });
  };

  if (anchor.measureInWindow) {
    anchor.measureInWindow((x, y, width, height) => {
      if (tryApply(x, y, width, height)) {
        return;
      }
      if (anchor.measure) {
        anchor.measure(
          (_fx, _fy, measuredWidth, measuredHeight, pageX, pageY) => {
            tryApply(pageX, pageY, measuredWidth, measuredHeight);
          },
        );
      }
    });
    return;
  }

  if (anchor.measure) {
    anchor.measure((_fx, _fy, width, height, pageX, pageY) => {
      tryApply(pageX, pageY, width, height);
    });
  }
}

function resolveAddScopeMenuPosition(anchorRect: {
  top: number;
  left: number;
  width: number;
  height: number;
}): { top: number; left: number } {
  const screen = Dimensions.get('window');
  let top = anchorRect.top + anchorRect.height + ADD_SCOPE_MENU_MARGIN_TOP;
  let left = anchorRect.left;

  if (
    top + ADD_SCOPE_MENU_ESTIMATED_HEIGHT >
    screen.height - ADD_SCOPE_MENU_EDGE
  ) {
    top =
      anchorRect.top -
      ADD_SCOPE_MENU_ESTIMATED_HEIGHT -
      ADD_SCOPE_MENU_MARGIN_TOP;
  }
  if (left + anchorRect.width > screen.width - ADD_SCOPE_MENU_EDGE) {
    left = screen.width - anchorRect.width - ADD_SCOPE_MENU_EDGE;
  }

  return {
    top: Math.max(ADD_SCOPE_MENU_EDGE, top),
    left: Math.max(ADD_SCOPE_MENU_EDGE, left),
  };
}

function getCreatePortal():
  | ((ui: React.ReactNode, container: Element) => React.ReactNode)
  | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    return require('react-dom').createPortal as (
      ui: React.ReactNode,
      container: Element,
    ) => React.ReactNode;
  } catch {
    return null;
  }
}

function resolveScopePickerRowState(
  stepId: string,
  enabledStepIds: readonly string[],
): ScopePickerRowState {
  if (isScopePickerComingSoon(stepId)) {
    return 'coming-soon';
  }
  if (enabledStepIds.includes(stepId)) {
    return 'added';
  }
  return 'addable';
}

export const AddScopeControl: React.FC<AddScopeControlProps> = ({
  disabled = false,
  stepVisibility,
}) => {
  const scope = useRequestScope();
  const {
    isOpen: menuOpen,
    close: closeMenu,
    open: openMenu,
  } = useScopeMenuCoordination(ADD_SCOPE_MENU_ID);
  const menuAnchorRef = useRef<View>(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [menuPositioned, setMenuPositioned] = useState(false);
  const useWebDropdown = Platform.OS === 'web';

  const pickerEntries = useMemo(
    () => getOptionalScopePickerCatalogEntries(),
    [],
  );

  const visibleAddableStepIds = useMemo(() => {
    const addable = scope?.addableStepIds ?? [];
    if (!stepVisibility) {
      return addable;
    }
    return filterAddableOptionalStepIdsByVisibility(addable, stepVisibility);
  }, [scope?.addableStepIds, stepVisibility]);

  const noAddableScopes = visibleAddableStepIds.length === 0;
  const interactionDisabled = !scope?.canManageScope;
  const buttonDisabled = disabled || interactionDisabled || noAddableScopes;

  const applyMenuPosition = useCallback(
    (x: number, y: number, width: number, height: number) => {
      const resolved = resolveAddScopeMenuPosition({
        top: y,
        left: x,
        width,
        height,
      });
      setMenuPosition({
        top: resolved.top,
        left: resolved.left,
        width,
      });
      if (!useWebDropdown) {
        setMenuPositioned(true);
      }
      return true;
    },
    [useWebDropdown],
  );

  const updateMenuPosition = useCallback(() => {
    const anchor = menuAnchorRef.current;
    if (!anchor) {
      return false;
    }
    measureAnchorInWindow(anchor, ({ x, y, width, height }) =>
      applyMenuPosition(x, y, width, height),
    );
    return true;
  }, [applyMenuPosition]);

  const openNativeMenu = useCallback(() => {
    setMenuPositioned(false);
    openMenu();
    scheduleMenuPositionUpdate(() => {
      updateMenuPosition();
      scheduleMenuPositionUpdate(updateMenuPosition);
    });
  }, [openMenu, updateMenuPosition]);

  const handleAdd = useCallback(
    (stepId: RequestFormStepId) => {
      scope?.addScope(stepId);
      closeMenu();
    },
    [closeMenu, scope],
  );

  const handleButtonPress = useCallback(() => {
    if (buttonDisabled) {
      return;
    }
    if (menuOpen) {
      closeMenu();
      return;
    }
    if (useWebDropdown) {
      openMenu();
      scheduleMenuPositionUpdate(() => {
        updateMenuPosition();
      });
      return;
    }
    openNativeMenu();
  }, [
    buttonDisabled,
    closeMenu,
    menuOpen,
    openMenu,
    openNativeMenu,
    updateMenuPosition,
    useWebDropdown,
  ]);

  useEffect(() => {
    if (buttonDisabled) {
      closeMenu();
    }
  }, [buttonDisabled, closeMenu]);

  useEffect(() => {
    if (!menuOpen) {
      setMenuPositioned(false);
      return undefined;
    }
    if (useWebDropdown) {
      updateMenuPosition();
      if (
        typeof window === 'undefined' ||
        typeof window.addEventListener !== 'function'
      ) {
        return undefined;
      }
      const handleReposition = () => {
        updateMenuPosition();
      };
      window.addEventListener('scroll', handleReposition, true);
      window.addEventListener('resize', handleReposition);
      return () => {
        window.removeEventListener('scroll', handleReposition, true);
        window.removeEventListener('resize', handleReposition);
      };
    }
    scheduleMenuPositionUpdate(updateMenuPosition);
    return undefined;
  }, [menuOpen, updateMenuPosition, useWebDropdown]);

  if (!scope?.showScopeControls) {
    return null;
  }

  const menuPanel = (
    <View
      style={[
        styles.menuPanel,
        menuPosition.width > 0 ? { width: menuPosition.width } : null,
      ]}
      testID="add-scope-menu"
      onStartShouldSetResponder={() => true}
      {...({
        [SCOPE_MENU_ROOT_ATTR]: `${ADD_SCOPE_MENU_ID}-panel`,
      } as Record<string, string>)}
    >
      <Text style={styles.menuHeading}>AVAILABLE SCOPES</Text>
      {pickerEntries.map(entry => {
        const rowState = resolveScopePickerRowState(
          entry.id,
          scope.enabledStepIds,
        );
        const label = entry.pickerLabel ?? entry.title;
        const isAddable =
          rowState === 'addable' &&
          visibleAddableStepIds.includes(entry.id as RequestFormStepId);

        return (
          <TouchableOpacity
            key={entry.id}
            style={[styles.menuItem, !isAddable && styles.menuItemDisabled]}
            {...({
              [SCOPE_MENU_ROOT_ATTR]: `${ADD_SCOPE_MENU_ID}-option-${entry.id}`,
            } as Record<string, string>)}
            onPress={
              isAddable
                ? () => handleAdd(entry.id as RequestFormStepId)
                : undefined
            }
            disabled={!isAddable}
            testID={`add-scope-option-${entry.id}`}
            accessibilityRole="button"
            accessibilityState={{
              disabled: !isAddable,
            }}
            accessibilityLabel={
              rowState === 'coming-soon'
                ? `${label}, coming soon`
                : rowState === 'added'
                ? `${label}, already added`
                : `Add ${label}`
            }
          >
            <Text
              style={[
                styles.menuItemLabel,
                !isAddable && styles.menuItemLabelMuted,
              ]}
              numberOfLines={1}
            >
              {label}
            </Text>
            {rowState === 'added' ? (
              <Text style={styles.menuItemBadge}>Added</Text>
            ) : null}
            {rowState === 'coming-soon' ? (
              <Text style={styles.menuItemBadge}>Coming soon</Text>
            ) : null}
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const createPortal = getCreatePortal();
  const webPortaledMenu =
    useWebDropdown &&
    menuOpen &&
    !buttonDisabled &&
    createPortal &&
    typeof document !== 'undefined'
      ? createPortal(
          <View
            style={[
              styles.webPortaledMenu,
              {
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width > 0 ? menuPosition.width : undefined,
              },
            ]}
          >
            {menuPanel}
          </View>,
          document.body,
        )
      : null;

  return (
    <View style={styles.container}>
      <View ref={menuAnchorRef} collapsable={false} style={styles.menuAnchor}>
        <TouchableOpacity
          style={[styles.addButton, buttonDisabled && styles.addButtonDisabled]}
          onPress={handleButtonPress}
          {...({
            [SCOPE_MENU_ROOT_ATTR]: ADD_SCOPE_MENU_ID,
          } as Record<string, string>)}
          disabled={buttonDisabled}
          testID="add-scope-button"
          accessibilityRole="button"
          accessibilityLabel="Add scope"
          accessibilityHint={
            interactionDisabled ? 'Click Edit to add scope' : undefined
          }
          accessibilityState={{ expanded: menuOpen, disabled: buttonDisabled }}
        >
          <Text
            style={[
              styles.addButtonText,
              buttonDisabled && styles.addButtonTextDisabled,
            ]}
          >
            + Add Scope
          </Text>
        </TouchableOpacity>
      </View>

      {webPortaledMenu}
      {!webPortaledMenu && menuOpen && !buttonDisabled ? (
        <Modal
          visible
          transparent
          animationType="fade"
          onRequestClose={closeMenu}
          statusBarTranslucent
        >
          <Pressable style={styles.menuModalBackdrop} onPress={closeMenu}>
            <View
              onStartShouldSetResponder={() => true}
              style={[
                styles.menuDropdownModal,
                menuPositioned
                  ? {
                      top: menuPosition.top,
                      left: menuPosition.left,
                      width:
                        menuPosition.width > 0 ? menuPosition.width : undefined,
                    }
                  : styles.menuDropdownModalHidden,
              ]}
            >
              {menuPanel}
            </View>
          </Pressable>
        </Modal>
      ) : null}
    </View>
  );
};

const ADD_SCOPE_BUTTON_TEXT_LINE_HEIGHT = lineHeight.formControl;
const ADD_SCOPE_BUTTON_VERTICAL_PADDING =
  spacing.formCompactActionPaddingVertical;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 0,
    ...Platform.select({
      web: {
        overflow: 'visible' as const,
      },
      ios: {
        overflow: 'visible' as const,
      },
    }),
  },
  menuAnchor: {
    position: 'relative',
    alignSelf: 'stretch',
    width: '100%',
    flexShrink: 0,
    ...Platform.select({
      web: {
        overflow: 'visible' as const,
      },
      ios: {
        overflow: 'visible' as const,
      },
    }),
  },
  addButton: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.primary,
    borderRadius: 8,
    paddingVertical: ADD_SCOPE_BUTTON_VERTICAL_PADDING,
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.backgroundPrimary,
    width: '100%',
    minHeight: REQUEST_FORM_SIDEBAR_HEADER_CONTROL_HEIGHT,
    ...Platform.select({
      ios: {
        overflow: 'visible' as const,
      },
      default: {},
    }),
  },
  addButtonDisabled: {
    opacity: 1,
    borderColor: colors.borderMedium,
    backgroundColor: colors.backgroundSecondary,
  },
  addButtonText: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: '600',
    lineHeight: ADD_SCOPE_BUTTON_TEXT_LINE_HEIGHT,
    textAlign: 'center',
  },
  addButtonTextDisabled: {
    color: colors.textMuted,
  },
  menuPanel: {
    backgroundColor: colors.backgroundPrimary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.borderLight,
    paddingTop: 10,
    paddingBottom: 6,
  },
  webPortaledMenu: {
    position: 'fixed',
    zIndex: 999999,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      },
    }),
  },
  menuModalBackdrop: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  menuDropdownModal: {
    position: 'absolute',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      },
      default: {
        elevation: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.12,
        shadowRadius: 12,
      },
    }),
  },
  menuDropdownModalHidden: {
    opacity: 0,
  },
  menuHeading: {
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.8,
    color: colors.textSecondary,
    paddingHorizontal: 12,
    paddingBottom: 8,
    textTransform: 'uppercase',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    minHeight: 40,
  },
  menuItemDisabled: {
    ...Platform.select({
      web: {
        cursor: 'default' as const,
      },
      default: {},
    }),
  },
  menuItemLabel: {
    flex: 1,
    fontSize: 14,
    color: colors.textPrimary,
    fontWeight: '500',
  },
  menuItemLabelMuted: {
    color: colors.textMuted,
    fontWeight: '400',
  },
  menuItemBadge: {
    flexShrink: 0,
    fontSize: 12,
    color: colors.textMuted,
    fontWeight: '400',
  },
});
